import { useMemo, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { cn } from "@/shared/lib/utils";
import { CopyButton } from "./atoms";
import { statusStyle, type RequestResult } from "./helpers";

type ResponseTab = "body" | "headers" | "curl";

export function ResponsePanel({
  result,
  curl,
}: {
  result: RequestResult;
  curl: string;
}) {
  const [activeTab, setActiveTab] = useState<ResponseTab>("body");

  const headerLines = useMemo(
    () =>
      [
        `Status: ${result.status} ${result.statusText}`,
        `Content-Type: application/json`,
        result.count != null ? `X-Total-Count: ${result.count}` : null,
      ]
        .filter(Boolean)
        .join("\n"),
    [result],
  );

  // Copy button mirrors whichever tab is open, so users get exactly the
  // payload they're looking at (body / headers / curl).
  const copyTarget: { value: string; label: string } =
    activeTab === "body"
      ? { value: result.body, label: "Response body" }
      : activeTab === "headers"
        ? { value: headerLines, label: "Response headers" }
        : { value: curl, label: "cURL" };

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted/30">
        <div className="flex items-center gap-3">
          <span
            className={cn(
              "inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold font-mono border",
              statusStyle(result.status),
            )}
          >
            {result.status} {result.statusText || (result.ok ? "OK" : "Error")}
          </span>
          <span className="text-xs text-muted-foreground">
            {result.latencyMs} ms
          </span>
        </div>
        <CopyButton value={copyTarget.value} label={copyTarget.label} />
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as ResponseTab)}
        className="w-full"
      >
        <TabsList className="rounded-none border-b border-border bg-transparent h-9 p-0 w-full justify-start gap-0">
          <TabsTrigger
            value="body"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none h-9 px-4 text-xs"
          >
            Body
          </TabsTrigger>
          <TabsTrigger
            value="headers"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none h-9 px-4 text-xs"
          >
            Headers
          </TabsTrigger>
          <TabsTrigger
            value="curl"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none h-9 px-4 text-xs"
          >
            cURL
          </TabsTrigger>
        </TabsList>

        <TabsContent value="body" className="m-0">
          <pre className="px-4 py-4 text-xs text-zinc-100 bg-zinc-950 dark:bg-zinc-900 font-mono overflow-x-auto whitespace-pre max-h-96 overflow-y-auto">
            {result.body || "(empty)"}
          </pre>
        </TabsContent>

        <TabsContent value="headers" className="m-0">
          <pre className="px-4 py-4 text-xs text-zinc-100 bg-zinc-950 dark:bg-zinc-900 font-mono overflow-x-auto whitespace-pre max-h-96 overflow-y-auto">
            {headerLines}
          </pre>
        </TabsContent>

        <TabsContent value="curl" className="m-0">
          <pre className="px-4 py-4 text-xs text-zinc-100 bg-zinc-950 dark:bg-zinc-900 font-mono overflow-x-auto whitespace-pre">
            {curl}
          </pre>
        </TabsContent>
      </Tabs>
    </div>
  );
}
